// ─── I dati dell'anagrafica e del borsellino ─────────────────────────────────
//
// ⚖️ Lo stato del server NON si duplica in uno stato locale: TanStack Query è
// l'unica copia. Qui in particolare il saldo — due copie dello stesso saldo
// divergono, e quando divergono nessuno se ne accorge finché qualcuno non
// quadra la cassa.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PremioDelSocio,
  AttivitaSocio,
  Borsellino,
  ElencoSoci,
  DomandaImportFile,
  EsitoImportFile,
  InvitoAlBot,
  ModificaSocio,
  MovimentoBorsellino,
  ScalaDeiLivelli,
  Socio,
} from '@pmo/shared';
import { chiama, scarica } from '../../lib/api.js';
import { inEuro } from '../../lib/esiti.js';

export function usaSoci(filtro: {
  q: string;
  active?: boolean | undefined;
  archived: boolean;
  cursor?: string | null;
}) {
  const parametri = new URLSearchParams();
  if (filtro.q) parametri.set('q', filtro.q);
  if (filtro.active !== undefined) parametri.set('active', String(filtro.active));
  if (filtro.archived) parametri.set('archived', 'true');
  if (filtro.cursor) parametri.set('cursor', filtro.cursor);

  return useQuery({
    queryKey: ['soci', filtro.q, filtro.active, filtro.archived, filtro.cursor ?? ''],
    queryFn: () => chiama<ElencoSoci>('GET', `/members?${parametri.toString()}`),
    /**
     * ⭐ LA RIGA CHE FA SEMBRARE VELOCE UNA RICERCA VELOCE. Senza, a ogni
     * battuta la lista si svuota e ricompare: il risultato arriva in 30 ms, ma
     * quello che si vede è un lampeggio. Tenendo i dati di prima finché non
     * arrivano quelli nuovi, la lista si **aggiorna** invece di rinascere.
     */
    placeholderData: (prima) => prima,
    staleTime: 15_000,
  });
}

/**
 * 🎾 La scala dei livelli del circolo, per leggerli e sceglierli a parole.
 * Cambia di rado — la si scrive dalle Impostazioni — quindi non si rilegge a
 * ogni scheda aperta.
 */
export const usaScalaDeiLivelli = () =>
  useQuery({
    queryKey: ['scala-livelli'],
    queryFn: () => chiama<ScalaDeiLivelli>('GET', '/members/livelli'),
    staleTime: 5 * 60_000,
  });

export const usaSocio = (id: string | null) =>
  useQuery({
    queryKey: ['socio', id],
    queryFn: () => chiama<Socio>('GET', `/members/${id}`),
    enabled: id !== null,
  });

export const usaAttivita = (id: string | null) =>
  useQuery({
    queryKey: ['attivita-socio', id],
    queryFn: () => chiama<AttivitaSocio>('GET', `/members/${id}/activity?limit=20`),
    enabled: id !== null,
  });

export const usaBorsellino = (id: string | null) =>
  useQuery({
    queryKey: ['borsellino', id],
    queryFn: () => chiama<Borsellino>('GET', `/wallet/${id}?limit=50`),
    enabled: id !== null,
  });

/** 🎁 I crediti premio di un socio (voce 33): a parte dal borsellino in euro. */
export const usaPremio = (id: string | null) =>
  useQuery({
    queryKey: ['premio', id],
    queryFn: () => chiama<PremioDelSocio>('GET', `/premio/${id}`),
    enabled: id !== null,
  });

/** Dopo ogni scrittura si rilegge: elenco, scheda e borsellino guardano gli stessi dati. */
function rileggiTutto(qc: ReturnType<typeof useQueryClient>, id: string): void {
  void qc.invalidateQueries({ queryKey: ['soci'] });
  void qc.invalidateQueries({ queryKey: ['socio', id] });
  void qc.invalidateQueries({ queryKey: ['borsellino', id] });
  void qc.invalidateQueries({ queryKey: ['attivita-socio', id] });
}

export function usaModificaSocio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'scheda del socio salvata' },
    mutationFn: (c: ModificaSocio) => chiama<Socio>('PATCH', `/members/${id}`, c),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

export function usaCreazioneSocio() {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: (d) => `socio ${(d as Socio).displayName} creato` },
    mutationFn: (c: Record<string, unknown>) => chiama<Socio>('POST', '/members', c),
    onSuccess: (s) => rileggiTutto(qc, s.id),
  });
}

export function usaArchiviazione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: (_d, v) => (v === 'archive' ? 'socio archiviato' : 'socio riportato fra gli attivi') },
    mutationFn: (verso: 'archive' | 'restore') => chiama<Socio>('POST', `/members/${id}/${verso}`),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/**
 * 🚨 LA CHIAVE DI IDEMPOTENZA LA GENERA CHI APRE LA FINESTRA, non chi preme.
 *
 * ⚖️ È qui che si vince il criterio della Fase 5 («si preme due volte ricarica
 * e il movimento resta UNO»). Generandola al momento del clic, due clic
 * farebbero due chiavi e quindi due movimenti — e il bottone disabilitato non
 * basta: non ferma un `Invio` ripetuto, non ferma una rete che rimanda la
 * richiesta, e non ferma due schede aperte sullo stesso socio.
 * 📌 L'unica difesa che regge è che i due tentativi portino **la stessa
 *    chiave**, e che a rifiutare il secondo sia il database.
 */
export function usaMovimentoBorsellino(id: string, chiave: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (_d, v) => {
        const m = v as { verso: 'recharge' | 'withdraw'; amountCents: number };
        return m.verso === 'recharge'
          ? `ricarica di ${inEuro(m.amountCents)} registrata nel borsellino`
          : `tolti ${inEuro(m.amountCents)} dal borsellino`;
      },
    },
    mutationFn: (m: {
      verso: 'recharge' | 'withdraw';
      amountCents: number;
      reason: string | null;
      /**
       * 💶 Con che mezzo il denaro è passato dal cassetto (Fase 6). `null` =
       * rettifica: il borsellino si muove e la cassa non se ne accorge.
       */
      method: 'cash' | 'card' | null;
    }) =>
      chiama<MovimentoBorsellino>('POST', `/wallet/${id}/${m.verso}`, {
        amountCents: m.amountCents,
        reason: m.reason,
        method: m.method,
        idempotencyKey: chiave,
      }),
    // 💶 Una ricarica in contanti è anche una riga di cassa: il riepilogo della
    // giornata e l'elenco degli incassi vanno riletti, o resterebbero a dire
    // una cifra di un minuto fa.
    onSuccess: () => {
      rileggiTutto(qc, id);
      void qc.invalidateQueries({ queryKey: ['incassi'] });
      void qc.invalidateQueries({ queryKey: ['riepilogo-cassa'] });
    },
  });
}

export function usaStornoMovimento(id: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'movimento del borsellino stornato' },
    mutationFn: (transactionId: string) =>
      chiama<MovimentoBorsellino>('POST', `/wallet/${id}/reverse`, { transactionId }),
    onSuccess: () => rileggiTutto(qc, id),
  });
}

/**
 * 📥 Voce 26: l'importazione dalla rubrica Google e da Matchpoint. Con
 * `apply: false` legge e basta; con `apply: true` scrive le righe scelte, e
 * allora l'elenco dei soci va riletto.
 */
export function usaImportFile() {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      // L'anteprima (senza `apply`) non cambia niente: tace.
      fatto: (d) => {
        const e = d as EsitoImportFile;
        return e.applicato ? `importazione fatta: ${e.creati} soci nuovi, ${e.aggiornati} aggiornati` : null;
      },
    },
    mutationFn: (d: Pick<DomandaImportFile, 'file' | 'scelte'> & { apply: boolean }) =>
      chiama<EsitoImportFile>('POST', '/members/import/file', d),
    onSuccess: (esito) => {
      if (esito.applicato) void qc.invalidateQueries({ queryKey: ['soci'] });
    },
  });
}

/**
 * L'export.
 *
 * ⛔ NON è un `<a href>`: il gettone di accesso sta in memoria, non in un
 * cookie, quindi un download avviato dal browser tornerebbe un 401. Passa da
 * `scarica()`, che mette l'intestazione al posto giusto e poi consegna il file.
 */
export function esporta(formato: 'csv' | 'xlsx', filtro: { q: string; archived: boolean }): Promise<void> {
  const p = new URLSearchParams({ format: formato });
  if (filtro.q) p.set('q', filtro.q);
  if (filtro.archived) p.set('archived', 'true');
  return scarica(`/members/export?${p.toString()}`, `soci.${formato}`);
}

// ── Il bot dei soci nella scheda (24/09/2026; link unico dalla voce 28) ─────

export const usaInvitoBot = (id: string) =>
  useQuery({
    queryKey: ['invito-bot', id],
    queryFn: () => chiama<InvitoAlBot>('GET', `/members/${id}/bot-invite`),
  });
